import { createContext, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ThemeContext = createContext({
  toggleColorMode: () => {},
  mode: 'dark',
});

export function useThemeContext() {
  return useContext(ThemeContext);
}

AppThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function AppThemeProvider({ children }) {
  const storedMode = localStorage.getItem('colorMode');
  const [mode, setMode] = useState(storedMode === 'light' || storedMode === 'dark' ? storedMode : 'dark');

  const toggleColorMode = () => {
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('colorMode', next);
      return next;
    });
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: '#0007FF',
            light: '#4B50FF',
            dark: '#0005B3',
            contrastText: '#ffffff',
          },
          ...(mode === 'light'
            ? {
                background: {
                  default: '#F4F6FB',
                  paper: '#FFFFFF',
                },
                text: {
                  primary: '#0D0D0D',
                  secondary: '#5A5A72',
                },
              }
            : {
                background: {
                  default: '#0A0A14',
                  paper: '#13131F',
                },
                text: {
                  primary: '#E8E8F0',
                  secondary: '#9090A8',
                },
              }),
        },
        typography: {
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          h1: { fontWeight: 700 },
          h2: { fontWeight: 700 },
          h3: { fontWeight: 700 },
          h4: { fontWeight: 600 },
          h5: { fontWeight: 600 },
          h6: { fontWeight: 600 },
          button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0.3 },
        },
        shape: {
          borderRadius: 12,
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: 10,
                padding: '8px 20px',
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              },
              containedPrimary: {
                background: 'linear-gradient(135deg, #0007FF 0%, #4B50FF 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0005B3 0%, #0007FF 100%)',
                },
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 16,
                boxShadow: mode === 'dark'
                  ? '0 4px 24px rgba(0,0,0,0.5)'
                  : '0 2px 16px rgba(0,7,255,0.07)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: mode === 'dark'
                    ? '0 8px 32px rgba(0,0,0,0.7)'
                    : '0 6px 28px rgba(0,7,255,0.14)',
                },
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                borderRadius: 16,
                backgroundImage: 'none',
              },
              elevation1: {
                boxShadow: mode === 'dark'
                  ? '0 2px 16px rgba(0,0,0,0.5)'
                  : '0 2px 12px rgba(0,7,255,0.06)',
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
                boxShadow: mode === 'dark'
                  ? '0 1px 0 rgba(255,255,255,0.06)'
                  : '0 1px 0 rgba(0,0,0,0.08)',
              },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                borderRight: mode === 'dark'
                  ? '1px solid rgba(255,255,255,0.06)'
                  : '1px solid rgba(0,0,0,0.06)',
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: 10,
                },
              },
            },
          },
          MuiListItemButton: {
            styleOverrides: {
              root: {
                borderRadius: 10,
                margin: '2px 8px',
                width: 'calc(100% - 16px)',
                '&.Mui-selected': {
                  backgroundColor: mode === 'dark'
                    ? 'rgba(0,7,255,0.2)'
                    : 'rgba(0,7,255,0.08)',
                  '&:hover': {
                    backgroundColor: mode === 'dark'
                      ? 'rgba(0,7,255,0.28)'
                      : 'rgba(0,7,255,0.12)',
                  },
                },
                '&:hover': {
                  backgroundColor: mode === 'dark'
                    ? 'rgba(255,255,255,0.05)'
                    : 'rgba(0,7,255,0.05)',
                },
              },
            },
          },
          MuiDivider: {
            styleOverrides: {
              root: {
                borderColor: mode === 'dark'
                  ? 'rgba(255,255,255,0.06)'
                  : 'rgba(0,0,0,0.06)',
              },
            },
          },
          MuiTableHead: {
            styleOverrides: {
              root: {
                '& .MuiTableCell-head': {
                  backgroundColor: mode === 'dark'
                    ? 'rgba(0,7,255,0.15)'
                    : 'rgba(0,7,255,0.06)',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                },
              },
            },
          },
          MuiTableRow: {
            styleOverrides: {
              root: {
                '&:hover': {
                  backgroundColor: mode === 'dark'
                    ? 'rgba(255,255,255,0.03)'
                    : 'rgba(0,7,255,0.02)',
                },
              },
            },
          },
          MuiModal: {
            styleOverrides: {
              root: {},
            },
          },
        },
      }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={{ toggleColorMode, mode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}
